import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  apiFetchAutonomyState,
  apiListAutonomyDecisions,
  apiPillarLifecycle,
} from '../studioApi.js'
import type {
  AuthorityTopicCandidate,
  Post,
  PillarLifecycleReport,
} from '../types'

// The autonomy resolver emits these exact verbs (server/services/
// autonomyPolicy.cjs). Any consumer that classifies decisions must
// match against this set — historically the hook checked 'approved'
// and 'blocked', which never appear in the table, so the autoPublished
// counter silently stayed at 0.
type AutonomyDecisionVerb =
  | 'auto-approved'
  | 'assist'
  | 'rejected'
  | 'skipped-killswitch'
  | 'skipped-cooldown'
  | 'skipped-cadence'
  | 'skipped-pillar-deprecated'

const SKIPPED_VERBS: ReadonlySet<string> = new Set([
  'skipped-killswitch',
  'skipped-cooldown',
  'skipped-cadence',
  'skipped-pillar-deprecated',
])

export interface AutonomyPolicy {
  contentType: string
  autonomyMode: 'auto' | 'assist' | 'manual'
  voiceScoreMin?: number | null
  requireGrounding?: boolean | null
  requirePillarMatch?: boolean | null
  dailyCap?: number | null
  notes?: string | null
  updatedAt?: string | null
  updatedBy?: string | null
}

export type PoliciesByContentType = Readonly<Record<string, AutonomyPolicy>>

// useAutonomySnapshot (Wave 24) — single hook that owns the dashboard
// command-strip state. Joins three endpoints into one operator-facing
// snapshot:
//   - /api/autonomy/state      → kill switch, incidents, cooldown, policies
//   - /api/autonomy/decisions  → recent decision tone (last 50)
//   - /api/pillars/lifecycle   → deprecation candidates + pending proposals
//
// Authority topic candidates and posts come from the existing context
// state (no fetch).
//
// All three fetches are independent — partial failure is tolerated:
// the snapshot exposes per-section error so the dashboard can degrade
// gracefully (e.g., show kill-switch state but mute the lifecycle card
// if /api/pillars/lifecycle is unreachable).

export interface AutonomyIncident {
  id: string
  kind: string
  severity: 'info' | 'warning' | 'error' | 'critical'
  summary: string
  details?: Record<string, unknown> | null
  createdAt: string
  resolvedAt?: string | null
}

export interface AutonomyState {
  killSwitchEngaged: boolean
  killSwitchReason?: string | null
  killSwitchActor?: string | null
  killSwitchEngagedAt?: string | null
}

export interface CooldownUsage {
  platform: string
  count: number
  capPerDay?: number | null
}

export interface DecisionToneRollup {
  windowHours: number
  total: number
  autoPublished: number
  blocked: number
  assistPending: number
  manualOnly: number
}

export interface TopicQueueHealth {
  pendingCount: number
  autoDiscoveredCount: number
  autoDiscoveredPercent: number
  oldestPendingAgeMs: number | null
}

export interface AutonomySnapshot {
  loading: boolean
  reload: () => Promise<void>
  state: AutonomyState | null
  stateError: string | null
  incidents: AutonomyIncident[]
  cooldown: CooldownUsage[]
  decisionTone: DecisionToneRollup
  decisionsError: string | null
  pillarLifecycle: PillarLifecycleReport | null
  pillarLifecycleError: string | null
  topicQueue: TopicQueueHealth
  todaysAutonomousPublishCount: number
  policiesByContentType: PoliciesByContentType
}

const EMPTY_TOPIC_QUEUE: TopicQueueHealth = Object.freeze({
  pendingCount: 0,
  autoDiscoveredCount: 0,
  autoDiscoveredPercent: 0,
  oldestPendingAgeMs: null,
})

/**
 * Compute the decision tone rollup from a list of recent decisions.
 * Pure helper — exported for tests.
 *
 * Bucket rules:
 *   - manualOnly:    autonomyMode === 'manual' (resolver was a no-op,
 *                    operator owns the publish decision)
 *   - autoPublished: decision === 'auto-approved' (the resolver said
 *                    publish; if publishState lags it just means the
 *                    executor hasn't fired yet — operator-facing metric
 *                    is "decisions where the gate said yes")
 *   - blocked:       decision === 'rejected' or any 'skipped-*'
 *                    (operator can't quickly intervene)
 *   - assistPending: decision === 'assist' or anything else
 */
export function computeDecisionTone(
  decisions: Array<Record<string, unknown>>,
  windowHours = 24,
  nowMs = Date.now()
): DecisionToneRollup {
  const cutoff = nowMs - windowHours * 60 * 60 * 1000
  let total = 0
  let autoPublished = 0
  let blocked = 0
  let assistPending = 0
  let manualOnly = 0
  for (const d of decisions) {
    const decidedAt = String(d.decidedAt || d.decided_at || '')
    const t = Date.parse(decidedAt)
    if (!Number.isFinite(t) || t < cutoff) continue
    total += 1
    const decision = String(d.decision || '') as AutonomyDecisionVerb
    const mode = String(d.autonomyMode || d.autonomy_mode || '')
    if (mode === 'manual') {
      manualOnly += 1
      continue
    }
    if (decision === 'auto-approved') {
      autoPublished += 1
    } else if (decision === 'rejected' || SKIPPED_VERBS.has(decision)) {
      blocked += 1
    } else {
      assistPending += 1
    }
  }
  return {
    windowHours,
    total,
    autoPublished,
    blocked,
    assistPending,
    manualOnly,
  }
}

/**
 * Compute topic-queue health from the authority topic candidates list.
 * Pure helper — exported for tests.
 */
export function computeTopicQueueHealth(
  candidates: AuthorityTopicCandidate[],
  nowMs = Date.now()
): TopicQueueHealth {
  const pending = candidates.filter((c) => c.status === 'pending')
  if (pending.length === 0) return EMPTY_TOPIC_QUEUE
  const autoDiscovered = pending.filter(
    (c) => typeof c.id === 'string' && c.id.startsWith('auto-discovery-')
  )
  const oldest = pending.reduce<number | null>((acc, c) => {
    const t = Date.parse(String(c.createdAt || ''))
    if (!Number.isFinite(t)) return acc
    if (acc == null || t < acc) return t
    return acc
  }, null)
  return {
    pendingCount: pending.length,
    autoDiscoveredCount: autoDiscovered.length,
    autoDiscoveredPercent: Math.round(
      (autoDiscovered.length / pending.length) * 100
    ),
    oldestPendingAgeMs: oldest != null ? nowMs - oldest : null,
  }
}

/**
 * Count autonomous publishes since start-of-day. Pure helper.
 *
 * Derives from the autonomy_decisions audit log — an `auto-approved`
 * row IS the publish event for autonomy purposes (the resolver only
 * writes that verb when every gate passed and the executor proceeded).
 *
 * The prior implementation read post.metadata.sourceType, which is
 * only set for cross-channel derivatives and auto-promoted topics.
 * A normal auto-mode publish leaves metadata untouched, so the count
 * was always 0 — the dashboard lied to the operator about what the
 * autonomy stack had done.
 */
export function countTodaysAutonomousPublishes(
  decisions: Array<Record<string, unknown>>,
  nowMs = Date.now()
): number {
  const startOfDay = new Date(nowMs)
  startOfDay.setHours(0, 0, 0, 0)
  const startMs = startOfDay.getTime()
  let n = 0
  for (const d of decisions) {
    if (String(d.decision || '') !== 'auto-approved') continue
    const decidedAt = String(d.decidedAt || d.decided_at || '')
    const t = Date.parse(decidedAt)
    if (!Number.isFinite(t) || t < startMs) continue
    n += 1
  }
  return n
}

/**
 * Map the autonomy_policies array (returned inline from /api/autonomy/state)
 * into a contentType → policy lookup table. Used by the dashboard footer
 * and the Compose right pane to render live mode chips instead of the
 * hardcoded "li-post assist" placeholders that drifted out of sync the
 * moment the operator promoted a content type.
 */
export function derivePoliciesByContentType(
  policies: ReadonlyArray<Partial<AutonomyPolicy>> | null | undefined
): PoliciesByContentType {
  if (!Array.isArray(policies)) return {}
  const out: Record<string, AutonomyPolicy> = {}
  for (const p of policies) {
    if (!p?.contentType || typeof p.contentType !== 'string') continue
    const mode = (p.autonomyMode as AutonomyPolicy['autonomyMode']) || 'manual'
    out[p.contentType] = {
      ...p,
      contentType: p.contentType,
      autonomyMode: mode,
    }
  }
  return out
}

interface UseAutonomySnapshotOptions {
  authorityTopicCandidates?: AuthorityTopicCandidate[]
  posts?: Post[]
  skipAutoFetch?: boolean
}

export function useAutonomySnapshot({
  authorityTopicCandidates = [],
  posts = [],
  skipAutoFetch = false,
}: UseAutonomySnapshotOptions = {}): AutonomySnapshot {
  const [state, setState] = useState<AutonomyState | null>(null)
  const [incidents, setIncidents] = useState<AutonomyIncident[]>([])
  const [cooldown, setCooldown] = useState<CooldownUsage[]>([])
  const [policies, setPolicies] = useState<
    ReadonlyArray<Partial<AutonomyPolicy>>
  >([])
  const [stateError, setStateError] = useState<string | null>(null)
  const [decisions, setDecisions] = useState<Array<Record<string, unknown>>>([])
  const [decisionsError, setDecisionsError] = useState<string | null>(null)
  const [pillarLifecycle, setPillarLifecycle] =
    useState<PillarLifecycleReport | null>(null)
  const [pillarLifecycleError, setPillarLifecycleError] = useState<
    string | null
  >(null)
  const [loading, setLoading] = useState<boolean>(false)

  const reload = useCallback(async () => {
    setLoading(true)
    const [stateResult, decisionsResult, lifecycleResult] = await Promise.all([
      apiFetchAutonomyState().then(
        (data) => ({ ok: true as const, data }),
        (err) => ({ ok: false as const, error: err })
      ),
      apiListAutonomyDecisions({ limit: 50 }).then(
        (data) => ({ ok: true as const, data }),
        (err) => ({ ok: false as const, error: err })
      ),
      apiPillarLifecycle().then(
        (data) => ({ ok: true as const, data }),
        (err) => ({ ok: false as const, error: err })
      ),
    ])

    if (stateResult.ok) {
      // The server response shape comes from autonomyRepo.getState() in
      // server/repos/autonomyRoutes.cjs:99 — the actual fields are
      // `killSwitchEnabled` / `killSwitchReason` /
      // `killSwitchTriggeredAt` / `killSwitchTriggeredBy`. The hook
      // before this fix read `raw.engaged` / `raw.reason` / `raw.actor`
      // / `raw.engagedAt`, which never matched. The bug stayed silent
      // because (a) tests mocked `state: { engaged: ... }` to match
      // the broken hook, not the server, and (b) the kill switch was
      // rarely engaged in dev so "always shows off" looked correct.
      // Audit (2026-05-09) caught it.
      const payload = stateResult.data as {
        state?: {
          killSwitchEnabled?: boolean
          killSwitchReason?: string
          killSwitchTriggeredBy?: string
          killSwitchTriggeredAt?: string
        }
        cooldown?: Array<CooldownUsage>
        incidents?: AutonomyIncident[]
        policies?: ReadonlyArray<Partial<AutonomyPolicy>>
      }
      const raw = payload?.state || {}
      setState({
        killSwitchEngaged: Boolean(raw.killSwitchEnabled),
        killSwitchReason: raw.killSwitchReason || null,
        killSwitchActor: raw.killSwitchTriggeredBy || null,
        killSwitchEngagedAt: raw.killSwitchTriggeredAt || null,
      })
      setCooldown(Array.isArray(payload?.cooldown) ? payload.cooldown : [])
      setIncidents(Array.isArray(payload?.incidents) ? payload.incidents : [])
      setPolicies(Array.isArray(payload?.policies) ? payload.policies : [])
      setStateError(null)
    } else {
      const err = stateResult.error
      setStateError(err instanceof Error ? err.message : String(err))
    }

    if (decisionsResult.ok) {
      const payload = decisionsResult.data as {
        decisions?: Array<Record<string, unknown>>
      }
      setDecisions(Array.isArray(payload?.decisions) ? payload.decisions : [])
      setDecisionsError(null)
    } else {
      const err = decisionsResult.error
      setDecisionsError(err instanceof Error ? err.message : String(err))
    }

    if (lifecycleResult.ok) {
      setPillarLifecycle(lifecycleResult.data as PillarLifecycleReport)
      setPillarLifecycleError(null)
    } else {
      const err = lifecycleResult.error
      setPillarLifecycleError(err instanceof Error ? err.message : String(err))
    }

    setLoading(false)
  }, [])

  useEffect(() => {
    if (skipAutoFetch) return
    void reload()
  }, [reload, skipAutoFetch])

  const decisionTone = useMemo(
    () => computeDecisionTone(decisions),
    [decisions]
  )
  const topicQueue = useMemo(
    () => computeTopicQueueHealth(authorityTopicCandidates),
    [authorityTopicCandidates]
  )
  // Derived from decisions (the autonomy_decisions audit log), not from
  // post metadata. The `posts` option is retained for back-compat with
  // existing callers but no longer participates in the count.
  const todaysAutonomousPublishCount = useMemo(
    () => countTodaysAutonomousPublishes(decisions),
    [decisions]
  )
  const policiesByContentType = useMemo(
    () => derivePoliciesByContentType(policies),
    [policies]
  )

  // `posts` retained as an option for forward-compat (callers may grow
  // post-derived signals here) — explicitly marked unused for now.
  void posts

  return {
    loading,
    reload,
    state,
    stateError,
    incidents,
    cooldown,
    decisionTone,
    decisionsError,
    pillarLifecycle,
    pillarLifecycleError,
    topicQueue,
    todaysAutonomousPublishCount,
    policiesByContentType,
  }
}
